import { captureStudyGroupViewRestorationTelemetry } from "@/lib/sentry";

const METRICS = [
  "retained_dataset_count",
  "retained_stream_size",
  "hard_limit_rejection",
  "lru_eviction",
  "restoration_result",
  "restoration_duration",
  "validation_result",
  "scroll_restoration",
  "update_notification",
  "update_refresh_retry",
  "update_apply_result",
  "update_endpoint_latency",
  "false_notification",
  "retained_content_invalidation",
  "acknowledgement_suspension",
] as const;

const SCOPE_TYPES = ["home", "channel", "events", "files", "links"] as const;

const FAILURE_CATEGORIES = [
  "storage_unavailable",
  "expired",
  "hard_limit",
  "dataset_missing",
  "snapshot_mismatch",
  "validation_failed",
  "validation_timeout",
  "access_lost",
  "update_check_failed",
  "stream_refresh_failed",
  "comment_refresh_failed",
  "positioning_failed",
  "restoration_timeout",
] as const;

export type StudyGroupViewRestorationMetric = (typeof METRICS)[number];
export type StudyGroupViewRestorationScopeType = (typeof SCOPE_TYPES)[number];
export type StudyGroupViewRestorationFailure =
  (typeof FAILURE_CATEGORIES)[number];

type MeasurementField = "count" | "durationMs";

type MetricPolicy = Readonly<{
  field: MeasurementField;
  buckets: readonly number[];
  failures?: readonly StudyGroupViewRestorationFailure[];
}>;

const UNIT_COUNT_BUCKETS = [1] as const;
const RESTORATION_FAILURES = [
  "storage_unavailable",
  "expired",
  "hard_limit",
  "dataset_missing",
  "snapshot_mismatch",
  "validation_failed",
  "validation_timeout",
  "access_lost",
  "positioning_failed",
  "restoration_timeout",
] as const;
const DURATION_BUCKETS_MS = [
  0, 50, 100, 250, 500, 1_000, 2_000, 5_000, 10_000, 30_000, 60_000,
] as const;

const METRIC_POLICIES: Record<StudyGroupViewRestorationMetric, MetricPolicy> = {
  retained_dataset_count: {
    field: "count",
    buckets: [0, 1, 2, 5, 10, 25],
  },
  retained_stream_size: {
    field: "count",
    buckets: [0, 1, 5, 10, 20, 50, 100, 250, 750],
  },
  hard_limit_rejection: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
    failures: ["hard_limit"],
  },
  lru_eviction: {
    field: "count",
    buckets: [1, 2, 5, 10, 25],
    failures: ["hard_limit"],
  },
  restoration_result: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
    failures: RESTORATION_FAILURES,
  },
  restoration_duration: {
    field: "durationMs",
    buckets: DURATION_BUCKETS_MS,
    failures: RESTORATION_FAILURES,
  },
  validation_result: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
    failures: ["validation_failed", "validation_timeout", "access_lost"],
  },
  scroll_restoration: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
    failures: ["positioning_failed", "restoration_timeout"],
  },
  update_notification: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
  },
  update_refresh_retry: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
    failures: ["stream_refresh_failed", "comment_refresh_failed"],
  },
  update_apply_result: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
    failures: ["stream_refresh_failed", "comment_refresh_failed"],
  },
  update_endpoint_latency: {
    field: "durationMs",
    buckets: DURATION_BUCKETS_MS,
    failures: ["update_check_failed"],
  },
  false_notification: {
    field: "count",
    buckets: UNIT_COUNT_BUCKETS,
  },
  retained_content_invalidation: {
    field: "count",
    buckets: [1, 2, 5, 10, 20, 50, 100],
  },
  acknowledgement_suspension: {
    field: "durationMs",
    buckets: DURATION_BUCKETS_MS,
  },
};

export type StudyGroupViewRestorationTelemetryEvent = Readonly<{
  metric: StudyGroupViewRestorationMetric;
  count?: number;
  durationMs?: number;
  scopeType?: StudyGroupViewRestorationScopeType;
  failure?: StudyGroupViewRestorationFailure;
}>;

export type StudyGroupViewRestorationTelemetrySink = (
  event: StudyGroupViewRestorationTelemetryEvent,
) => void;

export const STUDY_GROUP_VIEW_RESTORATION_TELEMETRY_EVENT =
  "dolphin:study-group-view-restoration-telemetry";

const allowedKeys = new Set([
  "metric",
  "count",
  "durationMs",
  "scopeType",
  "failure",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isOneOf = <T extends readonly string[]>(
  values: T,
  value: unknown,
): value is T[number] =>
  typeof value === "string" && (values as readonly string[]).includes(value);

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

const isNonNegativeDuration = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const bucketMeasurement = (value: number, buckets: readonly number[]): number =>
  buckets.find((bucket) => value <= bucket) ??
  buckets.at(-1) ??
  invalidTelemetry();

const invalidTelemetry = (): never => {
  throw new TypeError("Invalid study-group restoration telemetry payload");
};

export const createStudyGroupViewRestorationTelemetryEvent = (
  input: unknown,
): StudyGroupViewRestorationTelemetryEvent => {
  if (!isRecord(input)) return invalidTelemetry();
  if (Object.keys(input).some((key) => !allowedKeys.has(key))) {
    return invalidTelemetry();
  }
  if (!isOneOf(METRICS, input.metric)) return invalidTelemetry();
  const policy = METRIC_POLICIES[input.metric];
  const hasCount = input.count !== undefined;
  const hasDuration = input.durationMs !== undefined;
  if (policy.field === "count") {
    if (
      !hasCount ||
      hasDuration ||
      !isNonNegativeInteger(input.count) ||
      (policy.buckets === UNIT_COUNT_BUCKETS && input.count !== 1)
    ) {
      return invalidTelemetry();
    }
  } else if (
    !hasDuration ||
    hasCount ||
    !isNonNegativeDuration(input.durationMs)
  ) {
    return invalidTelemetry();
  }
  if (input.scopeType !== undefined && !isOneOf(SCOPE_TYPES, input.scopeType)) {
    return invalidTelemetry();
  }
  if (
    input.failure !== undefined &&
    (!isOneOf(FAILURE_CATEGORIES, input.failure) ||
      !policy.failures?.includes(input.failure))
  ) {
    return invalidTelemetry();
  }

  const measurement =
    policy.field === "count"
      ? { count: bucketMeasurement(input.count as number, policy.buckets) }
      : {
          durationMs: bucketMeasurement(
            input.durationMs as number,
            policy.buckets,
          ),
        };

  return Object.freeze({
    metric: input.metric,
    ...measurement,
    ...(input.scopeType !== undefined ? { scopeType: input.scopeType } : {}),
    ...(input.failure !== undefined ? { failure: input.failure } : {}),
  });
};

export const createStudyGroupViewRestorationTelemetryRecorder = (
  emit: StudyGroupViewRestorationTelemetrySink,
) => ({
  record(input: unknown): void {
    emit(createStudyGroupViewRestorationTelemetryEvent(input));
  },
});

export const recordStudyGroupViewRestorationTelemetry = (
  input: unknown,
): void => {
  const event = createStudyGroupViewRestorationTelemetryEvent(input);
  if (typeof window === "undefined") return;
  captureStudyGroupViewRestorationTelemetry(event);
  window.dispatchEvent(
    new CustomEvent(STUDY_GROUP_VIEW_RESTORATION_TELEMETRY_EVENT, {
      detail: event,
    }),
  );
};
